import React from 'react'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const TicketingOverview = () => (
  <>
    <Card gap='$4'>
      <Paragraph size='$5'>
        Guild includes a full ticketing system for Events, supporting both free
        and paid registration. You can sell tickets with multiple pricing tiers,
        offer discount codes, and manage orders — all from within the platform.
      </Paragraph>
      <DocScreenshot
        src='docs/events/event-page.png'
        alt='Event page showing the Attend button that changes to a Checkout button when paid ticketing tiers are configured'
        caption='Events with tickets show a Checkout button for attendees to purchase'
      />
    </Card>

    <Card gap='$4'>
      <DocSection id='ticketing-features' title='Ticketing Features'>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='ticket-diagonal' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Free vs paid Events</Paragraph> —
              By default, Events use free RSVPs. To charge for tickets, create one or
              more paid{' '}
              <Link href='/docs/events/tiers'>ticketing tiers</Link> in{' '}
              <Link href='/docs/events/settings'>Event Settings</Link>. You can also
              mix free and paid tiers on the same Event — for example, a free General
              Admission tier alongside a paid VIP tier.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='money' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Stripe integration</Paragraph> —
              Paid ticketing requires a connected Stripe account. Stripe handles
              payment processing, payouts, and regulatory compliance. Connect Stripe
              from your Guild's{' '}
              <Link href='/docs/guilds/monetization'>Monetization settings</Link>, or
              see the{' '}
              <Link href='/docs/events/stripe'>Setting Up Stripe</Link> guide for
              step-by-step instructions. Free tiers do not require a Stripe connection.
            </Paragraph>
          </XStack>
        </YStack>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='ticketing-tools' title='Ticketing Tools'>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='ticket-diagonal' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>
                <Link href='/docs/events/tiers'>Ticketing Tiers</Link>
              </Paragraph>{' '}
              — Create multiple ticket types with different names, prices, and
              quantities. Set sale windows, per-order limits, and cap availability for
              each tier independently.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='ribbon-filled' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>
                <Link href='/docs/events/discounts'>Discount Codes</Link>
              </Paragraph>{' '}
              — Offer percentage-based or fixed-amount discounts. Set usage limits and
              restrict discounts to specific tiers for targeted promotions like early
              bird pricing or group discounts.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='money' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>
                <Link href='/docs/events/orders'>Orders & Refunds</Link>
              </Paragraph>{' '}
              — View all ticket sales, track order statuses, process refunds, and
              export order data for your records.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='person' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>
                <Link href='/docs/events/purchase-flow'>Attendee Purchase Flow</Link>
              </Paragraph>{' '}
              — Learn what attendees experience when selecting a tier, entering payment
              details, and claiming their tickets.
            </Paragraph>
          </XStack>
        </YStack>
      </DocSection>
    </Card>
  </>
)

export default TicketingOverview
